import React, { useEffect, useMemo, useState } from "react";
import DetailSaveStatus from "../components/DetailSaveStatus";
import EntityPageScaffold from "../components/entity/EntityPageScaffold";
import { TableActionColumnHeader } from "../components/TableShell";
import AssetManagementSectionTabs from "../components/AssetManagementSectionTabs";
import { ASSET_TAB_RENT_LEDGER } from "../shared/utils/assetManagementTabs";
import { INVESTMENT_HOME_ASSET_CATEGORY } from "../shared/constants/rentalAsset";
import CrudListToolbar from "../components/entity/CrudListToolbar";
import useBlurAutoSave from "../shared/hooks/useBlurAutoSave";
import { useEntityListChrome } from "../shared/hooks/useEntityListChrome";
import { formatBatchSummaryMessage } from "../shared/utils/batchSummaryMessage";
import { sortRowsByConfig } from "../shared/utils/sortRowsByConfig";
import { bulkSequentialDelete } from "../shared/utils/bulkSequentialDelete";
import {
  formatSelectedCountSuffix,
  intersectSelectedIdsWithRows,
} from "../shared/utils/entityListSelection";
import { IconEdit, IconDelete, iconBtnStyle, RowActionIcons } from "../shared/icons/IconLib";

import { apiFetch } from "../shared/api/apiFetch";

const uiConfig = require("../uiConfig");
const { getMessageForFetchError } = require("../shared/utils/apiError");

const SORT_KEY_TYPES = { amount: "number" };

function toYmdString(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const yyyy = d.getUTCFullYear();
  const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(d.getUTCDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function normalizeNumber(value) {
  if (value == null) return "";
  const t = String(value).trim();
  if (!t) return "";
  const n = Number(t);
  return Number.isFinite(n) ? String(n) : "";
}

function leaseLabel(lease, propertyNameById) {
  if (!lease) return "—";
  const address =
    (lease.property_address != null && String(lease.property_address).trim()) ||
    propertyNameById.get(lease.asset_id) ||
    "—";
  const s = toYmdString(lease.start_date) || "—";
  const e = toYmdString(lease.end_date) || "—";
  const status = lease.status || "";
  return `${address} (${s} → ${e})${status ? ` • ${status}` : ""}`;
}

function RentLedger() {
  const userInfo = JSON.parse(sessionStorage.getItem("userInfo"));
  const account_id = userInfo?.account_id;
  const serverUrl = uiConfig.serverInfo.serverUrl;

  const [properties, setProperties] = useState([]);
  const [leases, setLeases] = useState([]);
  const [txns, setTxns] = useState([]);

  const [propertyFilter, setPropertyFilter] = useState("");
  const [leaseFilter, setLeaseFilter] = useState("");

  const {
    selectedIds,
    setSelectedIds,
    deleting,
    setDeleting,
    searchTerm,
    setSearchTerm,
    sortConfig,
    bannerError,
    setBannerError,
    viewMode,
    setViewMode,
    confirmDialog,
    setConfirmDialog,
    closeConfirmDialog,
    handleSort,
    handleSelectAll,
    handleSelectOne,
  } = useEntityListChrome({ initialViewMode: "table" });

  // Detail modal
  const [detailId, setDetailId] = useState(null);
  const [detailMode, setDetailMode] = useState(""); // '', 'view', 'edit', 'new'
  const [detailPropertyId, setDetailPropertyId] = useState("");
  const [detailLeaseId, setDetailLeaseId] = useState("");
  const [detailTxnName, setDetailTxnName] = useState("");
  const [detailTxnDate, setDetailTxnDate] = useState("");
  const [detailTxnType, setDetailTxnType] = useState("");
  const [detailAmount, setDetailAmount] = useState("");
  const [detailActiveField, setDetailActiveField] = useState("");
  const [detailInitialValues, setDetailInitialValues] = useState(null);

  useEffect(() => {
    refreshAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount
  }, []);

  function refreshAll() {
    if (!account_id) return;
    apiFetch(serverUrl + "/listAssets", {
      method: "POST",
      body: JSON.stringify({ account_id, category: INVESTMENT_HOME_ASSET_CATEGORY }),
    })
      .then((r) => r.json())
      .then((d) => setProperties(Array.isArray(d) ? d : []))
      .catch((e) => setBannerError(getMessageForFetchError(e)));

    apiFetch(serverUrl + "/listRentalLeases", {
      method: "POST",
      body: JSON.stringify({ account_id }),
    })
      .then((r) => r.json())
      .then((d) => setLeases(Array.isArray(d) ? d : []))
      .catch((e) => setBannerError(getMessageForFetchError(e)));

    apiFetch(serverUrl + "/listRentLedgerTxns", {
      method: "POST",
      body: JSON.stringify({ account_id }),
    })
      .then((r) => r.json())
      .then((d) => setTxns(Array.isArray(d) ? d : []))
      .catch((e) => setBannerError(getMessageForFetchError(e)));
  }

  const propertyNameById = useMemo(() => {
    const map = new Map();
    (properties || []).forEach((p) => map.set(String(p._id), p.name || "—"));
    return map;
  }, [properties]);

  const leaseById = useMemo(() => {
    const map = new Map();
    (leases || []).forEach((l) => map.set(l._id, l));
    return map;
  }, [leases]);

  const leaseOptions = useMemo(() => {
    const propId = (detailPropertyId || propertyFilter || "").trim();
    const list = (leases || []).filter((l) => !propId || l.asset_id === propId);
    return list.sort((a, b) => {
      const ap =
        (a.property_address != null && String(a.property_address).trim()) ||
        propertyNameById.get(a.asset_id) ||
        "";
      const bp =
        (b.property_address != null && String(b.property_address).trim()) ||
        propertyNameById.get(b.asset_id) ||
        "";
      if (ap !== bp) return ap.localeCompare(bp);
      const as = toYmdString(a.start_date);
      const bs = toYmdString(b.start_date);
      return bs.localeCompare(as);
    });
  }, [detailPropertyId, leases, propertyFilter, propertyNameById]);

  function getDetailFormValues(txn) {
    const lease = txn?.lease_id ? leaseById.get(txn.lease_id) : null;
    const property_id = txn?.property_id || lease?.asset_id || "";
    return {
      property_id,
      lease_id: txn?.lease_id || "",
      txn_name: txn?.txn_name || "",
      txn_date: toYmdString(txn?.txn_date),
      txn_type: txn?.txn_type || "",
      amount: txn?.amount == null ? "" : String(txn.amount),
    };
  }

  function applyDetailFormValues(values) {
    setDetailPropertyId(values?.property_id || "");
    setDetailLeaseId(values?.lease_id || "");
    setDetailTxnName(values?.txn_name || "");
    setDetailTxnDate(values?.txn_date || "");
    setDetailTxnType(values?.txn_type || "");
    setDetailAmount(normalizeNumber(values?.amount));
  }

  function openDetail(txn, mode = "view") {
    setDetailMode(mode);
    if (!txn && mode === "new") {
      setDetailId(null);
      const initialValues = getDetailFormValues(null);
      if (propertyFilter) initialValues.property_id = propertyFilter;
      if (leaseFilter) initialValues.lease_id = leaseFilter;
      setDetailInitialValues(initialValues);
      applyDetailFormValues(initialValues);
      setDetailActiveField("lease_id");
      return;
    }
    if (!txn) {
      setDetailMode("");
      return;
    }
    setDetailId(txn._id);
    const initialValues = getDetailFormValues(txn);
    setDetailInitialValues(initialValues);
    applyDetailFormValues(initialValues);
    setDetailActiveField(mode === "edit" ? "txn_name" : "");
  }

  function getDetailPayload() {
    return {
      account_id,
      property_id: (detailPropertyId || "").trim(),
      lease_id: (detailLeaseId || "").trim(),
      txn_name: (detailTxnName || "").trim(),
      txn_date: (detailTxnDate || "").trim(),
      txn_type: (detailTxnType || "").trim(),
      amount: (detailAmount || "").trim(),
    };
  }

  async function persistDetailChanges() {
    const payload = getDetailPayload();
    if (!payload.lease_id || !payload.txn_name || !payload.txn_date || !payload.amount) return;
    const isNew = detailMode === "new" || !detailId;
    const url = isNew ? serverUrl + "/createRentLedgerTxn" : serverUrl + "/updateRentLedgerTxn";
    const body = isNew ? payload : { ...payload, _id: detailId };
    try {
      if (isNew) {
        const resp = await apiFetch(url, { method: "POST", body: JSON.stringify(body) });
        const data = await resp.json().catch(() => null);
        if (!resp.ok) {
          setBannerError((data && data.message) || "Request failed.");
          return;
        }
        if (data && data._id) {
          setDetailId(data._id);
          setDetailMode("edit");
        }
      } else {
        const resp = await apiFetch(url, { method: "POST", body: JSON.stringify(body) });
        const text = await resp.text();
        if (!resp.ok) {
          setBannerError(text || "Request failed.");
          return;
        }
      }
      refreshAll();
      setDetailInitialValues(getDetailFormValues({ _id: detailId, ...payload, txn_date: payload.txn_date }));
    } catch (err) {
      setBannerError(getMessageForFetchError(err));
    }
  }

  function deleteDocument(txn) {
    const id = txn?._id;
    setConfirmDialog({
      open: true,
      title: "Delete ledger transaction",
      message: `Are you sure you want to delete "${txn?.txn_name || "this record"}"?`,
      onConfirm: async () => {
        try {
          const response = await apiFetch(serverUrl + "/deleteRentLedgerTxn", {
            method: "POST",
            body: JSON.stringify({ account_id, _id: id }),
          });
          const data = await response.text();
          if (!response.ok) {
            setBannerError(data || "Delete failed.");
            return;
          }
          setConfirmDialog({ open: true, title: "Rent Ledger", message: data, mode: "alert", onConfirm: null });
          const next = new Set(selectedIds);
          next.delete(id);
          setSelectedIds(next);
          refreshAll();
        } catch (err) {
          setBannerError(getMessageForFetchError(err));
        }
      },
    });
  }

  async function deleteSelected() {
    const selectedVisibleIds = getSelectedVisibleIds();
    if (selectedVisibleIds.size === 0) {
      setConfirmDialog({ open: true, title: "Delete ledger transactions", message: "Please select at least one record to delete", mode: "alert", onConfirm: null });
      return;
    }
    setConfirmDialog({
      open: true,
      title: "Delete ledger transactions",
      message: `Are you sure you want to delete ${selectedVisibleIds.size} record(s)?`,
      onConfirm: async () => {
        setDeleting(true);
        const idsToDelete = Array.from(selectedVisibleIds);
        const { successCount, errorCount, errors } = await bulkSequentialDelete(
          idsToDelete,
          async (_id) => {
            const response = await apiFetch(serverUrl + "/deleteRentLedgerTxn", {
              method: "POST",
              body: JSON.stringify({ account_id, _id }),
            });
            const data = await response.text();
            return { ok: response.ok, body: data };
          },
          getMessageForFetchError
        );
        setConfirmDialog({
          open: true,
          title: "Delete completed",
          message: formatBatchSummaryMessage("Delete completed!", successCount, errorCount, errors),
          onConfirm: null,
          mode: "alert",
        });
        setSelectedIds(new Set());
        refreshAll();
        setDeleting(false);
      },
    });
  }

  function getFilteredAndSortedData() {
    const searchLower = (searchTerm || "").toLowerCase().trim();
    const propId = (propertyFilter || "").trim();
    const leaseId = (leaseFilter || "").trim();

    const filtered = (txns || []).filter((t) => {
      if (propId && t.property_id !== propId) return false;
      if (leaseId && t.lease_id !== leaseId) return false;
      if (!searchLower) return true;
      const name = (t.txn_name || "").toLowerCase();
      const typ = (t.txn_type || "").toLowerCase();
      const amt = String(t.amount ?? "").toLowerCase();
      const date = toYmdString(t.txn_date);
      const lease = t.lease_id ? leaseById.get(t.lease_id) : null;
      const leaseText = lease ? leaseLabel(lease, propertyNameById).toLowerCase() : "";
      return name.includes(searchLower) || typ.includes(searchLower) || amt.includes(searchLower) || date.includes(searchLower) || leaseText.includes(searchLower);
    });
    return sortRowsByConfig(filtered, sortConfig, SORT_KEY_TYPES);
  }

  function getSelectedVisibleIds() {
    return intersectSelectedIdsWithRows(selectedIds, getFilteredAndSortedData());
  }

  const filteredRows = getFilteredAndSortedData();
  const selectedVisibleIds = intersectSelectedIdsWithRows(selectedIds, filteredRows);
  const selectedVisibleCount = selectedVisibleIds.size;
  const selectedCountSuffix = formatSelectedCountSuffix(selectedVisibleCount);

  const detailCurrentValues = useMemo(
    () => ({
      property_id: (detailPropertyId || "").trim(),
      lease_id: (detailLeaseId || "").trim(),
      txn_name: (detailTxnName || "").trim(),
      txn_date: (detailTxnDate || "").trim(),
      txn_type: (detailTxnType || "").trim(),
      amount: (detailAmount || "").trim(),
    }),
    [detailAmount, detailLeaseId, detailPropertyId, detailTxnDate, detailTxnName, detailTxnType]
  );

  const detailHasChanges = !!detailInitialValues && JSON.stringify(detailCurrentValues) !== JSON.stringify(detailInitialValues);

  const { isSaving: detailSaving, handleFieldBlur, handleFieldCommitKeyDown, saveIfNeeded } = useBlurAutoSave({
    hasChanges: detailHasChanges,
    saveFn: persistDetailChanges,
    onExitEdit: () => setDetailActiveField(""),
  });

  const detailAutoSaveEnabled = detailMode !== "new";
  const detailFieldBlurHandler = detailAutoSaveEnabled ? handleFieldBlur : undefined;
  const detailFieldCommitKeyDownHandler = detailAutoSaveEnabled ? handleFieldCommitKeyDown : undefined;

  const detailMissingRequired = !(detailCurrentValues.lease_id && detailCurrentValues.txn_name && detailCurrentValues.txn_date && detailCurrentValues.amount);

  const filteredLeaseOptionsForFilter = useMemo(() => {
    const propId = (propertyFilter || "").trim();
    const list = (leases || []).filter((l) => !propId || l.asset_id === propId);
    return list;
  }, [leases, propertyFilter]);

  return (
    <EntityPageScaffold
      title="Rent Ledger"
      titleTabs={<AssetManagementSectionTabs activeTab={ASSET_TAB_RENT_LEDGER} />}
      subtitle="Capture lease payments and ledger entries."
      bannerError={bannerError}
      onDismissBanner={() => setBannerError("")}
      confirmDialog={confirmDialog}
      onConfirmModal={() => {
        const fn = confirmDialog.onConfirm;
        closeConfirmDialog();
        if (fn) fn();
      }}
      onCancelModal={closeConfirmDialog}
      pageTopAfterModal={
        <>
          {(detailMode === "view" || detailMode === "edit" || detailMode === "new") && (
            <div className="detail-modal-overlay">
              <div role="button" tabIndex={0} className="detail-modal-backdrop" onClick={() => setDetailMode("")} onKeyDown={(e) => e.key === "Escape" && setDetailMode("")} aria-label="Close" />
              <div className="panel detail-modal-panel" style={{ maxWidth: 820 }}>
                <div className="panel-row" style={{ justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid #dee2e6", padding: "12px 16px" }}>
                  <h3 className="panel-title" style={{ margin: 0, fontSize: "1.1rem" }}>
                    {detailMode === "new" ? "New ledger transaction" : "Ledger transaction"}
                  </h3>
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    {detailMode === "new" && (
                      <button type="button" className="btn btn-primary" disabled={detailMissingRequired || detailSaving} onClick={async () => { await saveIfNeeded(); }}>
                        Create
                      </button>
                    )}
                    <DetailSaveStatus isSaving={detailSaving} hasChanges={detailHasChanges} />
                    <button type="button" className="btn-link btn-link-muted" onClick={() => setDetailMode("")} style={{ fontSize: "18px", lineHeight: 1, padding: "4px 8px" }} aria-label="Close">
                      ×
                    </button>
                  </div>
                </div>

                <div style={{ padding: "12px 16px" }}>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "12px 16px" }}>
                    <div style={{ display: "flex", flexDirection: "column" }}>
                      <label className="panel-label">Home</label>
                      <select
                        value={detailPropertyId}
                        onChange={(e) => {
                          const nextProp = e.target.value;
                          setDetailPropertyId(nextProp);
                          // Clear lease if it no longer matches
                          const lease = detailLeaseId ? leaseById.get(detailLeaseId) : null;
                          if (lease && nextProp && lease.asset_id !== nextProp) setDetailLeaseId("");
                        }}
                        onBlur={detailFieldBlurHandler}
                        onKeyDown={detailFieldCommitKeyDownHandler}
                        className="input input-full"
                      >
                        <option value="">(optional) Filter lease list…</option>
                        {(properties || []).map((p) => (
                          <option key={p._id} value={p._id}>
                            {p.name || "—"}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div style={{ display: "flex", flexDirection: "column" }}>
                      <label className="panel-label">Lease (required)</label>
                      {detailMode === "new" || detailActiveField === "lease_id" ? (
                        <select
                          autoFocus={detailMode === "new" || detailActiveField === "lease_id"}
                          value={detailLeaseId}
                          onChange={(e) => setDetailLeaseId(e.target.value)}
                          onBlur={detailFieldBlurHandler}
                          onKeyDown={detailFieldCommitKeyDownHandler}
                          className="input input-full"
                        >
                          <option value="">Select a lease…</option>
                          {leaseOptions.map((l) => (
                            <option key={l._id} value={l._id}>
                              {leaseLabel(l, propertyNameById)}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <button type="button" onClick={() => setDetailActiveField("lease_id")} className="btn-link" style={{ textAlign: "left", width: "100%", padding: "6px 0", color: "#212529", textDecoration: "none", fontWeight: 500 }}>
                          {leaseLabel(leaseById.get(detailLeaseId), propertyNameById)}
                        </button>
                      )}
                    </div>

                    <div style={{ display: "flex", flexDirection: "column" }}>
                      <label className="panel-label">Transaction name</label>
                      {detailMode === "new" || detailActiveField === "txn_name" ? (
                        <input
                          autoFocus={detailActiveField === "txn_name"}
                          type="text"
                          value={detailTxnName}
                          onChange={(e) => setDetailTxnName(e.target.value)}
                          onBlur={detailFieldBlurHandler}
                          onKeyDown={detailFieldCommitKeyDownHandler}
                          className="input input-full"
                          placeholder='e.g. "April Rent"'
                        />
                      ) : (
                        <button type="button" onClick={() => setDetailActiveField("txn_name")} className="btn-link" style={{ textAlign: "left", width: "100%", padding: "6px 0", color: "#212529", textDecoration: "none", fontWeight: 500 }}>
                          {detailTxnName || "—"}
                        </button>
                      )}
                    </div>

                    <div style={{ display: "flex", flexDirection: "column" }}>
                      <label className="panel-label">Transaction date</label>
                      {detailMode === "new" || detailActiveField === "txn_date" ? (
                        <input
                          autoFocus={detailActiveField === "txn_date"}
                          type="date"
                          value={detailTxnDate}
                          onChange={(e) => setDetailTxnDate(e.target.value)}
                          onBlur={detailFieldBlurHandler}
                          onKeyDown={detailFieldCommitKeyDownHandler}
                          className="input input-full"
                        />
                      ) : (
                        <button type="button" onClick={() => setDetailActiveField("txn_date")} className="btn-link" style={{ textAlign: "left", width: "100%", padding: "6px 0", color: "#212529", textDecoration: "none", fontWeight: 500 }}>
                          {detailTxnDate || "—"}
                        </button>
                      )}
                    </div>

                    <div style={{ display: "flex", flexDirection: "column" }}>
                      <label className="panel-label">Type</label>
                      {detailMode === "new" || detailActiveField === "txn_type" ? (
                        <input
                          autoFocus={detailActiveField === "txn_type"}
                          type="text"
                          value={detailTxnType}
                          onChange={(e) => setDetailTxnType(e.target.value)}
                          onBlur={detailFieldBlurHandler}
                          onKeyDown={detailFieldCommitKeyDownHandler}
                          className="input input-full"
                          placeholder='e.g. "Zelle", "Cash"'
                        />
                      ) : (
                        <button type="button" onClick={() => setDetailActiveField("txn_type")} className="btn-link" style={{ textAlign: "left", width: "100%", padding: "6px 0", color: "#212529", textDecoration: "none", fontWeight: 500 }}>
                          {detailTxnType || "—"}
                        </button>
                      )}
                    </div>

                    <div style={{ display: "flex", flexDirection: "column" }}>
                      <label className="panel-label">Amount</label>
                      {detailMode === "new" || detailActiveField === "amount" ? (
                        <input
                          autoFocus={detailActiveField === "amount"}
                          type="number"
                          step="0.01"
                          value={detailAmount}
                          onChange={(e) => setDetailAmount(e.target.value)}
                          onBlur={detailFieldBlurHandler}
                          onKeyDown={detailFieldCommitKeyDownHandler}
                          className="input input-full"
                        />
                      ) : (
                        <button type="button" onClick={() => setDetailActiveField("amount")} className="btn-link" style={{ textAlign: "left", width: "100%", padding: "6px 0", color: "#212529", textDecoration: "none", fontWeight: 500 }}>
                          {detailAmount || "—"}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          <CrudListToolbar
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            searchPlaceholder="Search name, type, amount, lease…"
            onClearSearch={() => setSearchTerm("")}
            middleSlot={
              <>
                <span className="toolbar-label">
                  <label htmlFor="ledger-property-filter">Home:</label>
                </span>
                <select
                  id="ledger-property-filter"
                  value={propertyFilter}
                  onChange={(e) => {
                    const next = e.target.value;
                    setPropertyFilter(next);
                    // reset lease filter if not in this property
                    if (leaseFilter) {
                      const lease = leaseById.get(leaseFilter);
                      if (lease && next && lease.asset_id !== next) setLeaseFilter("");
                    }
                  }}
                  className="input input-min-130"
                >
                  <option value="">All</option>
                  {(properties || []).map((p) => (
                    <option key={p._id} value={p._id}>
                      {p.name || "—"}
                    </option>
                  ))}
                </select>
                <span className="toolbar-divider" />
                <span className="toolbar-label">
                  <label htmlFor="ledger-lease-filter">Lease:</label>
                </span>
                <select id="ledger-lease-filter" value={leaseFilter} onChange={(e) => setLeaseFilter(e.target.value)} className="input input-min-130">
                  <option value="">All</option>
                  {filteredLeaseOptionsForFilter.map((l) => (
                    <option key={l._id} value={l._id}>
                      {leaseLabel(l, propertyNameById)}
                    </option>
                  ))}
                </select>
                <span className="toolbar-divider" />
              </>
            }
            onNew={() => openDetail(null, "new")}
            onDeleteSelected={deleteSelected}
            deleteDisabled={selectedVisibleCount === 0 || deleting}
            deleteCountSuffix={selectedCountSuffix}
            viewMode={viewMode}
            onViewModeChange={setViewMode}
          />
        </>
      }
      list={
        <div className="table-scroll">
          {viewMode === "table" && (
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th className="text-center">
                      <input
                        type="checkbox"
                        checked={filteredRows.length > 0 && filteredRows.every((obj) => selectedIds.has(obj._id))}
                        onChange={(e) => handleSelectAll(e, getFilteredAndSortedData)}
                        disabled={filteredRows.length === 0}
                      />
                    </th>
                    <th className="sortable" onClick={() => handleSort("txn_date")}>Date</th>
                    <th className="sortable" onClick={() => handleSort("txn_name")}>Name</th>
                    <th className="sortable" onClick={() => handleSort("txn_type")}>Type</th>
                    <th className="sortable" onClick={() => handleSort("amount")}>Amount</th>
                    <th>Lease</th>
                    <TableActionColumnHeader />
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map((t) => {
                    const lease = t.lease_id ? leaseById.get(t.lease_id) : null;
                    return (
                      <tr key={t._id}>
                        <td className="text-center">
                          <input type="checkbox" checked={selectedIds.has(t._id)} onChange={(e) => handleSelectOne(t._id, e.target.checked)} />
                        </td>
                        <td>{toYmdString(t.txn_date) || "—"}</td>
                        <td>
                          <button type="button" className="btn-link" onClick={() => openDetail(t, "view")}>
                            {t.txn_name || "—"}
                          </button>
                        </td>
                        <td>{t.txn_type || "—"}</td>
                        <td>{t.amount == null ? "—" : t.amount}</td>
                        <td style={{ whiteSpace: "pre-wrap" }}>{lease ? leaseLabel(lease, propertyNameById) : "—"}</td>
                        <td className="text-center">
                          <RowActionIcons>
                            <button type="button" onClick={() => openDetail(t, "edit")} className="btn-link" style={iconBtnStyle} title="Edit">
                              <IconEdit size={18} />
                            </button>
                            <button type="button" onClick={() => deleteDocument(t)} className="btn-link-danger" style={iconBtnStyle} title="Delete">
                              <IconDelete size={18} />
                            </button>
                          </RowActionIcons>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {viewMode === "card" && (
            <div className="cards-wrapper">
              {filteredRows.map((t) => {
                const lease = t.lease_id ? leaseById.get(t.lease_id) : null;
                return (
                  <div key={t._id} className="card">
                    <div className="card-title" style={{ marginBottom: 4 }}>
                      <button type="button" className="btn-link card-title" onClick={() => openDetail(t, "view")}>
                        {t.txn_name || "—"}
                      </button>
                    </div>
                    <div className="card-meta-muted">{toYmdString(t.txn_date) || "—"}</div>
                    <div className="panel-hint">{t.txn_type || "—"}</div>
                    <div className="panel-hint">Amount: {t.amount == null ? "—" : t.amount}</div>
                    <div className="card-meta-muted" style={{ whiteSpace: "pre-wrap" }}>
                      {lease ? leaseLabel(lease, propertyNameById) : "—"}
                    </div>
                    <div className="card-footer">
                      <RowActionIcons variant="inline">
                      <button type="button" onClick={() => openDetail(t, "edit")} className="btn-link" style={iconBtnStyle} title="Edit">
                        <IconEdit size={18} />
                      </button>
                      <button type="button" onClick={() => deleteDocument(t)} className="btn-link-danger" style={iconBtnStyle} title="Delete">
                        <IconDelete size={18} />
                      </button>
                      </RowActionIcons>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      }
    />
  );
}

export default RentLedger;

